import clsx from 'clsx'
import Button from 'gu-ui-kit/Button'
import Dropdown from 'gu-ui-kit/Dropdown'
import DropdownItem from 'gu-ui-kit/Dropdown/Item'
import Grid from 'gu-ui-kit/Grid/Container'
import Icon from 'gu-ui-kit/Icon'
import Popover from 'gu-ui-kit/Popover'
import Typography from 'gu-ui-kit/Typography'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useWriteToClipboard from 'utils/hooks/useWriteToClipboard'
import styles from 'components/Drawer/subcomponents/DrawerFooter/subcomponents/UserMenu/user-menu.module.scss'
import drawerFooterPopoverStyles from 'components/Drawer/subcomponents/DrawerFooter/drawer-footer-popover.module.scss'
import { useDrawerFooterMenuInteractions } from 'components/Drawer/subcomponents/DrawerFooter/drawer-footer-popover'
import UserSwitcher from 'components/Header/subcomponents/UserSwitcher'
import useSwitchedUser from 'utils/hooks/useSwitchedUser'
import { CURRENCIES } from 'constants/app-config'
import { notificationShow } from 'modules/Notifications/actions'
import { LOGOUT } from 'modules/Auth/types'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { selectAppUser } from 'modules/App/store/selectors'
import DrawerFooterAnimatedIcon from 'components/Drawer/subcomponents/DrawerFooter/subcomponents/DrawerFooterAnimatedIcon'

const UserMenu = ({ className, collapse = false, footerAnchorRef = null }) => {
  const { isPopoverViewport, popoverState, triggerBindings, popoverBindings, contentHoverHandlers } =
    useDrawerFooterMenuInteractions({
      collapse,
      footerAnchorRef,
      id: 'userMenuDropdown',
    })
  const writeToClipboard = useWriteToClipboard()

  const pathGen = useGeneratePathI18n()

  const { id, email, is_switch_user_available, is_agency_account_available } = useSelector(selectAppUser)

  const dispatch = useDispatch()
  const { t } = useTranslation(['common'])
  const { switchedUser, dropSwitchedUser } = useSwitchedUser()

  const toClipboard = useCallback((value) => {
    writeToClipboard(value)

    dispatch(
      notificationShow({
        type: 'success',
        message: 'common::notifications.success_copy_notification',
      }),
    )
  }, [])

  return (
    <>
      <Button
        {...triggerBindings}
        noLabel
        className={clsx(styles['user-menu__avatar'], className, {
          [styles['user-menu__avatar_switched']]: switchedUser,
          [styles['user-menu__avatar_switch-available']]: is_switch_user_available,
        })}
        grey={!switchedUser}
        size={'s'}
        startIcon={<DrawerFooterAnimatedIcon name={'user'} />}
        title={t('common::user_menu.user_account')}
        variant={'ghost'}
      />

      <Popover
        className={
          isPopoverViewport
            ? clsx(drawerFooterPopoverStyles['popover'], {
                [drawerFooterPopoverStyles['popover_collapsed']]: collapse,
                [drawerFooterPopoverStyles['popover_user']]: !collapse,
              })
            : styles['popover']
        }
        {...popoverBindings}
        bodyClassName={clsx({
          [styles['popover-body_switched']]: switchedUser,
          [drawerFooterPopoverStyles['body_expanded']]: isPopoverViewport && !collapse,
          [drawerFooterPopoverStyles['body_collapsed']]: isPopoverViewport && collapse,
        })}
      >
        <Dropdown
          className={clsx(styles['dropdown'], {
            [drawerFooterPopoverStyles['dropdown_collapsed']]: isPopoverViewport && collapse,
            [drawerFooterPopoverStyles['dropdown_expanded']]: isPopoverViewport && !collapse,
          })}
          {...contentHoverHandlers}
        >
          <Grid
            className={styles['user-menu__user-profile']}
            direction={'column'}
            gap={16}
          >
            {is_switch_user_available && !is_agency_account_available && (
              <UserSwitcher
                className={clsx(styles['user-menu__user-switcher'], {
                  [styles['user-menu__user-switcher_collapsed']]: collapse,
                })}
                email={email}
                id={id}
              />
            )}

            {switchedUser && (
              <div className={styles['user-menu__logged-with-previous-admin']}>
                {t('common::user_menu.logged_with_user')}
              </div>
            )}
            <Grid
              direction={'row'}
              gap={4}
              wrap={false}
            >
              <Grid
                className={clsx(
                  styles['user-menu__avatar'],
                  styles['user-menu__user-profile-avatar'],
                )}
                fluid={false}
              >
                <Icon
                  className={'icon'}
                  color={switchedUser ? 'Accent-Notice-Primary' : 'Interface-GU-Primary'}
                  name={'user'}
                  width={20}
                />
              </Grid>

              <Grid
                alignItems={'flex-start'}
                className={styles['user-menu__user-profile-info']}
                direction={'column'}
              >
                <Grid
                  direction={'column'}
                  gap={4}
                >
                  <Typography
                    className={styles['user-menu__user-profile-email']}
                    color={'Label-Fifth'}
                    component={'div'}
                    title={email}
                    variant={'Body-15'}
                  >
                    {email}
                  </Typography>

                  <Typography
                    className={styles['user-menu__user-profile-id-copy']}
                    color={'Label-Secondary'}
                    component={'span'}
                    variant={'Body-15'}
                    onClick={() => toClipboard(id)}
                  >
                    <span className={styles['user-menu__user-profile-id']}>ID: {id}</span>
                    <Icon
                      color={'inherit'}
                      name={'copy-link'}
                      width={20}
                    />
                  </Typography>
                </Grid>
              </Grid>

              {switchedUser && (
                <Grid
                  direction={'column'}
                  fluid={false}
                  justifyContent={'center'}
                >
                  <Button
                    noLabel
                    className={styles['user-menu__action-button']}
                    color={'Interface-Eighth'}
                    size={'s'}
                    startIcon={<Icon name={'login'} />}
                    title={t('common::user_menu.logout')}
                    variant={'ghost'}
                    onClick={dropSwitchedUser}
                  />
                </Grid>
              )}
            </Grid>
            <DropdownItem
              noHover
              className={styles['user-menu__logout-button']}
              component={'a'}
              href={pathGen(ROUTES.AUTH_LOGOUT)}
              size={'m'}
              startIcon={
                <Icon
                  color={'Interface-Eighth'}
                  name={'login'}
                  width={20}
                />
              }
              onClick={(e) => {
                e.preventDefault()
                popoverState.close()
                dispatch({ type: LOGOUT })
              }}
            >
              {t('common::user_menu.logout')}
            </DropdownItem>
          </Grid>
        </Dropdown>
      </Popover>
    </>
  )
}

UserMenu.propTypes = {
  className: PropTypes.string,
  collapse: PropTypes.bool,
  footerAnchorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  balance: PropTypes.shape({
    currency: PropTypes.oneOf(CURRENCIES),
  }),
}

export default UserMenu
